import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  Paper,
  Fade
} from '@mui/material';
import {
  ContentCut as ScissorsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import CategorySelectionStep from './steps/CategorySelectionStep';
import LocationSelectionStep from './steps/LocationSelectionStep';
import ContactDetailsStep from './steps/ContactDetailsStep';

const ProfessionalSignUp = ({ onBack }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    salonName: '',
    ownerName: '',
    phone: '',
    address: '',
    city: '',
    region: '',
    category: '',
    description: '',
    hasPhysicalLocation: ''
  });
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { signup } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  useEffect(() => {
    if (selectedAddress) {
      setFormData(prev => ({
        ...prev,
        city: selectedAddress.address_components?.city || '',
        region: selectedAddress.address_components?.state || '',
        address: selectedAddress.query || ''
      }));
    }
  }, [selectedAddress]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
    setError('');
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setError('');
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    if (!formData.salonName || !formData.ownerName || !formData.phone || !formData.category) {
      setError(t('auth.fillAllFields'));
      return;
    }

    if (!selectedAddress) {
      setError(t('auth.pleaseEnterAddress'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signup(formData.email, formData.password, {
        salonName: formData.salonName,
        ownerName: formData.ownerName,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        region: formData.region,
        category: formData.category,
        description: formData.description,
        role: 'salon'
      });

      setSuccess(t('auth.salonCreatedSuccess'));
      // Ne pas rediriger automatiquement - laisser Auth.js gérer la vérification d'email
    } catch (error) {
      console.error('Signup error:', error);
      setError(error.message || t('auth.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <CategorySelectionStep
            formData={formData}
            setFormData={setFormData}
            onBack={onBack}
            onNext={nextStep}
          />
        );
      case 1:
        return (
          <LocationSelectionStep
            formData={formData}
            setFormData={setFormData}
            onBack={prevStep}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <ContactDetailsStep
            formData={formData}
            handleInputChange={handleInputChange}
            selectedAddress={selectedAddress}
            setSelectedAddress={setSelectedAddress}
            onBack={prevStep}
            onSubmit={handleSubmit}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  if (isMobile) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Mobile Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
            p: 2,
            color: '#212121',
            textAlign: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(33,33,33,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1
            }}
          >
            <ScissorsIcon sx={{ fontSize: 20, color: '#212121' }} />
          </Box>
          
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
            {t('auth.professionalAccount')}
          </Typography>
          
          <Typography variant="caption" sx={{ opacity: 0.8, color: '#424242' }}>
            {t('auth.stepOf', { current: currentStep + 1, total: 3 })}
          </Typography>
        </Box>

        {/* Mobile Progress */}
        <Box sx={{ px: 2, py: 1, backgroundColor: '#2a2a2a' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1
            }}
          >
            {[0, 1, 2].map((step) => (
              <Box
                key={step}
                sx={{
                  flex: 1,
                  height: 4,
                  backgroundColor: step <= currentStep ? '#f5f5dc' : 'rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  mx: 0.5,
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>
            {currentStep === 0 && t('auth.establishmentType')}
            {currentStep === 1 && t('auth.location')}
            {currentStep === 2 && t('auth.contactDetails')}
          </Typography>
        </Box>

        {/* Mobile Content */}
        <Box sx={{ flex: 1, p: 2, backgroundColor: '#2a2a2a', overflow: 'auto' }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                color: '#ef4444',
                fontSize: '0.875rem',
                '& .MuiAlert-icon': {
                  color: '#ef4444'
                }
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 2,
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                color: '#10b981',
                fontSize: '0.875rem',
                '& .MuiAlert-icon': {
                  color: '#10b981'
                }
              }}
            >
              {success}
            </Alert>
          )}

          <Fade in={true} timeout={300}>
            <Box>
              {renderCurrentStep()}
            </Box>
          </Fade>
        </Box>
      </Box>
    );
  }

  // Desktop version
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #212121 0%, #424242 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#2a2a2a',
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          {/* Desktop Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f5f5dc 0%, #e8e8c8 100%)',
              p: 4,
              color: '#212121',
              textAlign: 'center',
              position: 'relative'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: 'rgba(33,33,33,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <ScissorsIcon sx={{ fontSize: 30, color: '#212121' }} />
              </Box>
              
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
                {t('auth.createProfessionalAccount')}
              </Typography>
              
              <Typography variant="body1" sx={{ opacity: 0.8, color: '#424242' }}>
                {t('auth.joinThousandsProfessionals')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 4, backgroundColor: '#2a2a2a' }}>
            {/* Desktop Progress Stepper */}
            <Stepper 
              activeStep={currentStep} 
              sx={{ 
                mb: 4,
                '& .MuiStepLabel-label': {
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.875rem'
                },
                '& .MuiStepLabel-label.Mui-active': {
                  color: '#f5f5dc'
                },
                '& .MuiStepLabel-label.Mui-completed': {
                  color: '#f5f5dc'
                },
                '& .MuiStepIcon-root': {
                  color: 'rgba(255,255,255,0.3)',
                  '&.Mui-active': {
                    color: '#f5f5dc'
                  },
                  '&.Mui-completed': {
                    color: '#f5f5dc'
                  }
                },
                '& .MuiStepConnector-line': {
                  borderColor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <Step>
                <StepLabel>{t('auth.establishmentType')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('auth.location')}</StepLabel>
              </Step>
              <Step>
                <StepLabel>{t('auth.contactDetails')}</StepLabel>
              </Step>
            </Stepper>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#ef4444',
                  '& .MuiAlert-icon': {
                    color: '#ef4444'
                  }
                }}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3,
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  '& .MuiAlert-icon': {
                    color: '#10b981'
                  }
                }}
              >
                {success}
              </Alert>
            )}

            {/* Desktop Content */}
            <Fade in={true} timeout={300}>
              <Box>
                {renderCurrentStep()}
              </Box>
            </Fade>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ProfessionalSignUp;